import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Activity } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  usePatient,
  useUpdatePatient,
  useArchivePatient,
  useDecryptCpr,
  usePatientAge,
} from '@/hooks/usePatients';
import { usePatientAppointments } from '@/hooks/usePatientAppointments';
import { patientMutationErrorText } from '@/lib/patientMutationError';
import { canEnterPatientCpr, isValidCprEntry } from '@/lib/cprEntry';
import { contactFieldErrorTexts } from '@/lib/contactFields';
import {
  applyDefaultTelephoneTypes,
  emptyTelephoneDraft,
  firstStamkortFocusId,
  focusPatientField,
  stamkortFieldErrorTexts,
  stamkortPayload,
  telephoneSubscriberDrafts,
  type TelephoneSubscriberDraft,
} from '@/lib/patientStamkortFields';
import { stamkortMessagesFromT } from '@/components/patient/PatientStamkortFields';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { PageLayout } from '@/components/ui/PageLayout';
import { Skeleton } from '@/components/ui/Skeleton';
import { EditPatientSlideOver, CPR_CLEAR } from '@/components/patient/EditPatientSlideOver';
import { PatientHeader } from '@/components/patient/PatientHeader';
import { RevealCprModal } from '@/components/patient/RevealCprModal';
import { JournalSection } from '@/components/journal/JournalSection';
import { JournalWriteView } from '@/components/journal/JournalWriteView';
import { PatientDocumentsSection } from '@/components/documents/PatientDocumentsSection';
import { SygeforsikringTab } from '@/components/sygeforsikring/SygeforsikringTab';
import { InvoiceTab } from '@/components/patient/InvoiceTab';
import { AppointmentTab } from '@/components/patient/AppointmentTab';
import { ActivityTab } from '@/components/patient/ActivityTab';
import { CommunicationTab } from '@/components/patient/CommunicationTab';
import { PunchCardTab } from '@/components/patient/PunchCardTab';
import { TreatmentCourseTab } from '@/components/patient/TreatmentCourseTab';
import { TaskTab } from '@/components/patient/TaskTab';
import { FormsAssignmentTab } from '@/components/patient/FormsAssignmentTab';
import { ConsentTab } from '@/components/patient/ConsentTab';
import { PatientLabResultsTab } from '@/components/lab/PatientLabResultsTab';
import { PatientReferralSagsakterTab } from '@/components/patient/PatientReferralSagsakterTab';
import { PatientHenvisningWorkspace, henvisningCreatedPath } from '@/components/patient/PatientHenvisningWorkspace';
import { PatientMedcomWorkspace } from '@/components/patient/PatientMedcomWorkspace';
import { PatientStaffAssignmentsTab } from '@/components/patient/PatientStaffAssignmentsTab';
import { RelationsTab, RelatedPersonNotice } from '@/components/patient/RelationsTab';
import { useClinicSdEnabled } from '@/hooks/useSygeforsikring';
import { useServicesList } from '@/hooks/useServices';
import { useStartWherebyMeeting, useWherebyEnabled } from '@/hooks/useWhereby';
import { VideoMeetingsTab } from '@/components/patient/VideoMeetingsTab';
import { PatientReportsTab } from '@/components/patient/PatientReportsTab';
import { usePatientReportsEnabled } from '@/hooks/usePatientReports';
import { openExternalMeeting } from '@/lib/whereby';
import { useClinicType } from '@/hooks/useClinicType';
import { useClinic } from '@/hooks/useClinicSettings';
import { useClinicCommunicateConnection } from '@/hooks/useCareCommunications';
import { PatientMedcomUnreadCount } from '@/components/patient/PatientCareCommunicationTab';
import {
  henvisningBoxFromSearch,
  henvisningCreatedDetailPath,
  parsePatientRecordPath,
  patientCorrespondenceNewPath,
  patientGgopNewPath,
  patientHenvisningNewPath,
  patientHenvisningPath,
  patientRecordActiveTab,
  patientRecordPath,
  patientTabClickSearchParams,
  patientTabPath,
} from '@/lib/patientRecordPaths';
import { useReferralIdByPatient } from '@/hooks/useReferrals';
import { isClinicModuleEnabled, readActivatedModules } from '@/lib/clinicModules';
import type { Json } from '@/types/database.generated';
import { NoAccess } from '@/components/NoAccess';
import {
  resolvePatientTab,
  type PendingTab,
  type TabAccess,
} from '@/lib/patientTabs';
import { journalGatedTabAccess, requestedPatientPageTab } from '@/lib/patientPageTabs';
import {
  applyVisibleTabReorder,
  isDefaultPatientTabOrder,
  moveTab,
  setPatientTabHidden,
  shownPatientTabs,
} from '@/lib/patientTabOrder';
import { usePatientTabPrefs } from '@/hooks/usePatientTabPrefs';
import {
  SortablePatientTabs,
  type PatientTabVisibilityOption,
  type SortablePatientTab,
} from '@/components/patient/SortablePatientTabs';

interface AddressShape {
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

const EMPTY_ADDRESS: AddressShape = {
  street: '',
  zip_code: '',
  city: '',
  country: 'Danmark',
};

function readAddress(value: Json | null): AddressShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_ADDRESS;
  }
  const v = value as Record<string, Json>;
  return {
    street: typeof v.street === 'string' ? v.street : '',
    // We accept both `zip` and `zip_code` for v1 compat.
    zip_code: typeof v.zip_code === 'string' ? v.zip_code : typeof v.zip === 'string' ? v.zip : '',
    city: typeof v.city === 'string' ? v.city : '',
    country: typeof v.country === 'string' ? v.country : 'Danmark',
  };
}

export function PatientDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const recordView = parsePatientRecordPath(location.pathname, searchParams);
  const pathTab = patientRecordActiveTab(recordView);
  const requestedTab = requestedPatientPageTab(pathTab, searchParams.get('tab'));
  const { isClinicAdmin, isPlatformAdmin, user, hasPermission } = useAuth();
  const {
    order: savedTabOrder,
    hidden: hiddenTabs,
    patientInfoCollapsed,
    setOrder: setSavedTabOrder,
    setHidden: setHiddenTabs,
    setPatientInfoCollapsed,
    reset: resetTabPrefs,
  } = usePatientTabPrefs();
  const { activeClinicId } = useActiveClinic();
  const { data: patient, isLoading, error } = usePatient(id);
  const { data: ageYears = null } = usePatientAge(id);
  const update = useUpdatePatient();
  const archive = useArchivePatient();
  const decrypt = useDecryptCpr();
  const { data: sdInfo } = useClinicSdEnabled(activeClinicId);
  const { data: wherebyEnabled } = useWherebyEnabled(activeClinicId);
  const { data: patientReportsEnabled } = usePatientReportsEnabled(activeClinicId);
  const { data: services } = useServicesList(activeClinicId);
  const startVideoMeeting = useStartWherebyMeeting(activeClinicId);
  const { isDentist } = useClinicType();
  const { data: clinic, isLoading: clinicLoading } = useClinic(activeClinicId);
  const { data: communicateConnection, isLoading: communicateLoading } =
    useClinicCommunicateConnection(activeClinicId);
  const referralsModuleEnabled = isClinicModuleEnabled(
    readActivatedModules(clinic?.activated_modules),
    'referrals',
  );
  const { data: referralId } = useReferralIdByPatient(referralsModuleEnabled ? id : undefined);
  const { data: patientAppointments } = usePatientAppointments(id);

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldsSubmitted, setFieldsSubmitted] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [telephoneSubscribers, setTelephoneSubscribers] = useState<TelephoneSubscriberDraft[]>([]);
  const [address, setAddress] = useState<AddressShape>(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');
  const [cave, setCave] = useState('');
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);
  const [appointmentEmailOptOut, setAppointmentEmailOptOut] = useState(false);
  const [preferredLocale, setPreferredLocale] = useState<'da-DK' | 'en-DK'>('da-DK');
  const [cprChange, setCprChange] = useState<string | null>(null);

  const [revealedCpr, setRevealedCpr] = useState<string | null>(null);
  const [cprModalOpen, setCprModalOpen] = useState(false);
  const cprHideTimer = useRef<number | null>(null);

  type TabType =
    | 'appointments'
    | 'treatment_courses'
    | 'activity'
    | 'journal'
    | 'lab_results'
    | 'documents'
    | 'invoices'
    | 'punch_cards'
    | 'sygeforsikring'
    | 'communication'
    | 'consent'
    | 'tasks'
    | 'questionnaires'
    | 'dental'
    | 'henvisning'
    | 'sagsakter'
    | 'tilknytninger'
    | 'relations'
    | 'medcom'
    | 'video_meetings'
    | 'patient_reports';
  const [activeTab, setActiveTab] = useState<TabType>(
    () => requestedTab ?? 'appointments',
  );
  const [tabDenied, setTabDenied] = useState(false);
  // Nested henvisning/korrespondance/ggop paths, or ?tab=<value>, until permission resolves.
  const pendingUrlTab = useRef<PendingTab<TabType> | null>(
    requestedTab ? { tab: requestedTab, fallback: null } : null,
  );
  const journalAppointmentId = searchParams.get('appointment');
  const journalEntryId = searchParams.get('entry');
  const journalEntryDate = searchParams.get('date');
  const highlightMessageId = searchParams.get('message');
  const highlightThreadId = searchParams.get('thread');

  useEffect(() => {
    if (!requestedTab) return;
    pendingUrlTab.current = { tab: requestedTab, fallback: null };
    setActiveTab(requestedTab);
  }, [requestedTab]);

  useEffect(() => {
    const purchase = searchParams.get('purchase');
    if (purchase !== 'succeeded' && purchase !== 'cancelled') return;
    if (purchase === 'succeeded') {
      toast.success(
        t(
          'patients.punch_cards.purchase_success',
          'Payment completed. The punch card will appear shortly.',
        ),
      );
      setActiveTab('punch_cards');
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'punch_cards');
      next.delete('purchase');
      setSearchParams(next, { replace: true });
      return;
    } else {
      toast(t('patients.punch_cards.purchase_cancelled', 'Online payment was cancelled'));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('purchase');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  // Tab permission checks
  const canViewAppointments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'appointments', 'view'),
  );
  const canViewTreatmentCourses = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'treatment_courses', 'view'),
  );
  const canEditTreatmentCourses = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'treatment_courses', 'update'),
  );
  const canViewJournal = Boolean(
    patient?.clinic_id && hasPermission(patient.clinic_id, 'journal', 'view'),
  );
  const canViewDocuments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'documents', 'view'),
  );
  const canUploadDocuments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'documents', 'create'),
  );
  const canUpdateDocuments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'documents', 'update'),
  );
  const canDeleteDocuments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'documents', 'delete'),
  );
  const canViewReferrals = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'referrals', 'view'),
  );
  const canViewInvoices = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'invoices', 'view'),
  );
  const canViewPunchCards = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'punch_cards', 'view'),
  );
  const canViewSygeforsikring = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'sygeforsikring', 'view'),
  );
  const canCreateAppointments = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'appointments', 'create'),
  );
  const canViewVideoMeetings = Boolean(canViewAppointments && wherebyEnabled);
  const canViewPatientReports = Boolean(
    activeClinicId &&
      hasPermission(activeClinicId, 'patient_reports', 'view') &&
      patientReportsEnabled,
  );
  const canViewCommunication = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'communication', 'view'),
  );
  const canSendSms = Boolean(activeClinicId && hasPermission(activeClinicId, 'sms', 'create'));
  const canSendMessages = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'messages', 'create'),
  );
  const canViewQuestionnaires = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'questionnaires', 'view'),
  );
  const canViewDental = Boolean(activeClinicId && hasPermission(activeClinicId, 'dental', 'view'));
  const canViewConsent = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'consent', 'view'),
  );
  const canViewTasks = Boolean(activeClinicId && hasPermission(activeClinicId, 'tasks', 'view'));
  const canViewRelations = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'relations', 'view'),
  );
  const canViewActivity = Boolean(
    canViewAppointments ||
    canViewJournal ||
    canViewInvoices ||
    canViewCommunication ||
    canViewQuestionnaires ||
    canViewPunchCards,
  );

  useEffect(() => {
    if (!patient) return;
    setFirstName(patient.first_name);
    setLastName(patient.last_name);
    setEmail(patient.email ?? '');
    setJobTitle(patient.job_title ?? '');
    const drafts = telephoneSubscriberDrafts(patient.telephone_subscribers);
    setTelephoneSubscribers(drafts.length > 0 ? drafts : [emptyTelephoneDraft()]);
    setAddress(readAddress(patient.address));
    setNotes(patient.notes ?? '');
    setCave(patient.cave ?? '');
    setMarketingEmail(patient.marketing_consent_email);
    setMarketingSms(patient.marketing_consent_sms);
    setAppointmentEmailOptOut(Boolean(patient.appointment_email_opt_out));
    setPreferredLocale((patient.preferred_locale as 'da-DK' | 'en-DK') ?? 'da-DK');
    setCprChange(null);
    setRevealedCpr(null);
    setCprModalOpen(false);
    if (cprHideTimer.current) window.clearTimeout(cprHideTimer.current);
  }, [patient]);

  useEffect(() => {
    return () => {
      if (cprHideTimer.current) window.clearTimeout(cprHideTimer.current);
    };
  }, []);

  const isRelated = patient?.status === 'related';
  const canViewAssignments = Boolean(
    !isRelated && patient && hasPermission(patient.clinic_id, 'patient_assignments', 'view'),
  );
  const canEditAssignments = Boolean(
    patient &&
    activeClinicId === patient.clinic_id &&
    hasPermission(patient.clinic_id, 'patient_assignments', 'update'),
  );
  const medcomAccess: TabAccess = (() => {
    if (isRelated || !patient || !activeClinicId || activeClinicId !== patient.clinic_id) {
      return false;
    }
    if (!hasPermission(patient.clinic_id, 'medcom', 'view')) return false;
    if (communicateLoading) return 'pending';
    return communicateConnection?.status === 'active';
  })();
  const canViewMedcom = medcomAccess === true;
  const canViewXdis = Boolean(
    canViewMedcom && patient && hasPermission(patient.clinic_id, 'medcom_xdis', 'view'),
  );
  const canViewGgop = Boolean(
    canViewMedcom && patient && hasPermission(patient.clinic_id, 'medcom_ggop', 'view'),
  );
  const henvisningAccess: TabAccess = (() => {
    if (isRelated) return false;
    if (!patient || !activeClinicId) return 'pending';
    if (clinicLoading) return 'pending';
    if (canViewReferrals && referralsModuleEnabled) return true;
    if (communicateLoading) return 'pending';
    return canViewMedcom;
  })();
  const canViewHenvisning = henvisningAccess === true;

  // Auto-switch to first available tab if current tab is no longer accessible
  useEffect(() => {
    const tabPermissions: Record<TabType, TabAccess> = {
      appointments: !isRelated && canViewAppointments,
      treatment_courses: !isRelated && canViewTreatmentCourses,
      journal: !isRelated && canViewJournal,
      lab_results: journalGatedTabAccess({
        patientLoaded: Boolean(patient),
        isRelated,
        canViewJournal,
      }),
      dental: !isRelated && canViewDental && isDentist,
      documents: !isRelated && canViewDocuments,
      henvisning: henvisningAccess,
      sagsakter: !isRelated && canViewReferrals && referralsModuleEnabled && Boolean(referralId),
      tilknytninger: canViewAssignments,
      invoices: !isRelated && canViewInvoices,
      punch_cards: !isRelated && canViewPunchCards,
      sygeforsikring: !isRelated && canViewSygeforsikring && Boolean(sdInfo?.enabled),
      video_meetings: !isRelated && canViewVideoMeetings,
      patient_reports: !isRelated && canViewPatientReports,
      communication: !isRelated && canViewCommunication,
      consent: !isRelated && canViewConsent,
      tasks: !isRelated && canViewTasks,
      questionnaires: !isRelated && canViewQuestionnaires,
      activity: !isRelated && canViewActivity,
      relations: canViewRelations,
      medcom: medcomAccess,
    };

    if (pathTab) {
      const access = tabPermissions[pathTab];
      if (access === 'pending') {
        pendingUrlTab.current = { tab: pathTab, fallback: null };
        setTabDenied(false);
        if (activeTab !== pathTab) setActiveTab(pathTab);
        return;
      }
    }

    const next = resolvePatientTab(activeTab, tabPermissions, pendingUrlTab.current);
    pendingUrlTab.current = next.pending;
    setTabDenied(next.denied);
    if (next.tab !== activeTab) setActiveTab(next.tab);
  }, [
    activeTab,
    pathTab,
    henvisningAccess,
    clinicLoading,
    canViewAppointments,
    canViewTreatmentCourses,
    canViewActivity,
    canViewJournal,
    canViewDental,
    canViewDocuments,
    canViewReferrals,
    canViewInvoices,
    canViewPunchCards,
    canViewSygeforsikring,
    canViewCommunication,
    canViewConsent,
    canViewTasks,
    canViewRelations,
    canViewQuestionnaires,
    isDentist,
    isRelated,
    referralsModuleEnabled,
    referralId,
    sdInfo?.enabled,
    canViewVideoMeetings,
    canViewPatientReports,
    medcomAccess,
    canViewAssignments,
    patient,
  ]);

  const nextAppointment = useMemo(() => {
    const now = Date.now();
    const upcoming = (patientAppointments ?? [])
      .filter(
        (apt) =>
          apt.status !== 'cancelled' &&
          apt.status !== 'no_show' &&
          new Date(apt.starts_at).getTime() >= now,
      )
      .sort((a, b) => +new Date(a.starts_at) - +new Date(b.starts_at));
    return upcoming[0] ?? null;
  }, [patientAppointments]);

  if (isLoading) {
    return (
      <PageLayout fill>
        <div aria-busy>
          <div className="flex gap-s4">
            <Skeleton className="h-[72px] w-[72px] rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="mt-s2 h-4 w-80" />
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !patient) {
    return (
      <PageLayout>
        <Banner
          tone="error"
          title={t('patients.error_not_found')}
          action={
            <Button variant="secondary" onClick={() => navigate('/patients')}>
              {t('patients.back_to_list')}
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const canEdit = Boolean(
    activeClinicId &&
    activeClinicId === patient.clinic_id &&
    (isPlatformAdmin || isClinicAdmin(patient.clinic_id) || true), // any clinic member can edit
  );
  const canReferToHospital = Boolean(
    patient.status !== 'related' &&
    activeClinicId &&
    activeClinicId === patient.clinic_id &&
    hasPermission(patient.clinic_id, 'medcom', 'create') &&
    communicateConnection?.status === 'active',
  );
  const canWriteGgop = Boolean(
    patient.status !== 'related' &&
    activeClinicId &&
    activeClinicId === patient.clinic_id &&
    hasPermission(patient.clinic_id, 'medcom_ggop', 'create') &&
    communicateConnection?.status === 'active',
  );
  const canArchive = canEdit && patient.status !== 'related';

  if (recordView.kind === 'journal-write') {
    if (!canViewJournal || patient.status === 'related') {
      return (
        <PageLayout>
          <NoAccess />
        </PageLayout>
      );
    }
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <JournalWriteView
          clinicId={patient.clinic_id}
          patient={patient}
          currentUserId={user?.id}
          ageYears={ageYears}
        />
      </div>
    );
  }

  // Tab bar: default order + visibility here; the user's saved order only reorders the
  // visible tabs (lib/patientTabOrder.ts, stored in user_ui_preferences).
  const notRelated = patient.status !== 'related';
  const tabDefs: Record<
    PatientTabKey,
    { visible: boolean; name: string; label?: ReactNode; onSelect?: () => void }
  > = {
    appointments: {
      visible: canViewAppointments && notRelated,
      name: t('patients.tabs.appointments'),
    },
    treatment_courses: {
      visible: canViewTreatmentCourses && notRelated,
      name: t('patients.tabs.treatment_courses'),
    },
    relations: { visible: canViewRelations, name: t('patients.tabs.relations') },
    journal: { visible: notRelated && canViewJournal, name: t('patients.tabs.journal') },
    lab_results: {
      visible: notRelated && canViewJournal,
      name: t('patients.tabs.lab_results', 'Labsvar'),
    },
    medcom: {
      visible: canViewMedcom,
      name: t('patients.tabs.medcom'),
      label: (
        <>
          {t('patients.tabs.medcom')}
          <PatientMedcomUnreadCount
            clinicId={activeClinicId ?? patient.clinic_id}
            patientId={patient.id}
          />
        </>
      ),
    },
    dental: {
      visible: notRelated && isDentist && canViewDental,
      name: t('dental.module.openButton'),
      label: (
        <span className="flex items-center gap-1.5">
          <Activity className="h-4 w-4" />
          {t('dental.module.openButton')}
        </span>
      ),
      onSelect: () => navigate(`/patients/${patient.id}/tand`),
    },
    documents: { visible: notRelated && canViewDocuments, name: t('patients.tabs.documents') },
    henvisning: {
      visible: notRelated && henvisningAccess !== false,
      name: t('patients.tabs.henvisning'),
    },
    sagsakter: {
      visible: notRelated && referralsModuleEnabled && Boolean(referralId) && canViewReferrals,
      name: t('patients.tabs.sagsakter'),
    },
    tilknytninger: { visible: canViewAssignments, name: t('patients.tabs.tilknytninger') },
    invoices: { visible: notRelated && canViewInvoices, name: t('patients.tabs.invoices') },
    punch_cards: { visible: notRelated && canViewPunchCards, name: t('patients.tabs.punch_cards') },
    sygeforsikring: {
      visible: notRelated && Boolean(sdInfo?.enabled) && canViewSygeforsikring,
      name: t('patients.tabs.sygeforsikring'),
    },
    video_meetings: {
      visible: notRelated && canViewVideoMeetings,
      name: t('patients.tabs.video_meetings'),
    },
    patient_reports: {
      visible: notRelated && canViewPatientReports,
      name: t('patients.tabs.patient_reports'),
    },
    communication: {
      visible: notRelated && canViewCommunication,
      name: t('patients.tabs.communication', 'Kommunikation'),
    },
    consent: { visible: notRelated && canViewConsent, name: t('patients.tabs.consent', 'Consent') },
    tasks: { visible: notRelated && canViewTasks, name: t('patients.tabs.tasks', 'Tasks') },
    questionnaires: {
      visible: notRelated && canViewQuestionnaires,
      name: t('patients.tabs.questionnaires', 'Questionnaires'),
    },
    activity: {
      visible: notRelated && canViewActivity,
      name: t('patients.tabs.activity', 'Activity'),
    },
  };
  // Permitted tabs, minus the ones the user hid under 'Tilpas faner'. The first permitted tab
  // can't be hidden; a hidden tab opened via ?tab= is shown while it is active (temporary).
  const permittedTabKeys = PATIENT_TAB_DEFAULT_ORDER.filter((key) => tabDefs[key].visible);
  const {
    tabs: orderedTabKeys,
    locked: lockedTabKey,
    temporary: temporaryTabKey,
  } = shownPatientTabs({
    defaultOrder: PATIENT_TAB_DEFAULT_ORDER,
    savedOrder: savedTabOrder,
    permitted: permittedTabKeys,
    hidden: hiddenTabs,
    activeTab,
  });
  const hiddenTabSet = new Set(hiddenTabs);
  const tabVisibilityOptions: PatientTabVisibilityOption[] = shownPatientTabs({
    defaultOrder: PATIENT_TAB_DEFAULT_ORDER,
    savedOrder: savedTabOrder,
    permitted: permittedTabKeys,
    hidden: [],
  }).tabs.map((key) => ({
    key,
    name: tabDefs[key].name,
    locked: key === lockedTabKey,
    shown: key === lockedTabKey || !hiddenTabSet.has(key),
  }));
  const selectTab = (key: TabType) => {
    setActiveTab(key);
    if (key === 'henvisning') {
      navigate(patientHenvisningPath(patient.id), { replace: true });
      return;
    }
    const next = patientTabClickSearchParams(
      recordView.kind === 'tab' ? searchParams : undefined,
      key,
    );
    if (recordView.kind !== 'tab') {
      navigate(`${patientRecordPath(patient.id)}?${next}`, { replace: true });
    } else {
      setSearchParams(next, { replace: true });
    }
  };

  const handleTabVisibility = (key: string, shown: boolean) => {
    setHiddenTabs(
      setPatientTabHidden(
        PATIENT_TAB_DEFAULT_ORDER,
        hiddenTabs,
        key as PatientTabKey,
        !shown,
        lockedTabKey,
      ),
    );
    // Hiding the open tab moves to the first tab instead of keeping it as a temporary tab.
    if (!shown && key === activeTab && lockedTabKey && lockedTabKey !== 'dental') {
      selectTab(lockedTabKey as TabType);
    }
  };
  const orderedTabs: SortablePatientTab[] = orderedTabKeys.map((key) => {
    const def = tabDefs[key];
    const isLink = key === 'dental';
    return {
      key,
      name: def.name,
      label: def.label ?? def.name,
      active: !isLink && activeTab === key,
      onSelect: def.onSelect ?? (() => selectTab(key as TabType)),
      temporary: key === temporaryTabKey,
    };
  });
  const handleTabReorder = (activeKey: string, overKey: string) => {
    const nextVisible = moveTab(
      orderedTabKeys,
      activeKey as PatientTabKey,
      overKey as PatientTabKey,
    );
    const next = applyVisibleTabReorder(PATIENT_TAB_DEFAULT_ORDER, savedTabOrder, nextVisible);
    setSavedTabOrder(isDefaultPatientTabOrder(PATIENT_TAB_DEFAULT_ORDER, next) ? null : next);
  };
  const canRevealCpr = Boolean(
    activeClinicId === patient.clinic_id &&
    hasPermission(patient.clinic_id, 'patients_cpr', 'view'),
  );
  const canEditCpr = Boolean(
    activeClinicId === patient.clinic_id &&
    canEnterPatientCpr(
      (resource, action) => hasPermission(patient.clinic_id, resource, action),
      'update',
    ),
  );

  const fieldMessages = {
    invalidEmail: t('patients.errors.invalid_email'),
    invalidPhone: t('patients.errors.invalid_phone'),
  };
  const fieldErrors = contactFieldErrorTexts(fieldsSubmitted, email, '', fieldMessages);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const subscribers = applyDefaultTelephoneTypes(telephoneSubscribers);
    if (subscribers.some((row, index) => row.code !== telephoneSubscribers[index]?.code)) {
      setTelephoneSubscribers(subscribers);
    }
    const fields = contactFieldErrorTexts(true, email, '', fieldMessages);
    const stamkort = stamkortFieldErrorTexts(true, jobTitle, subscribers, stamkortMessagesFromT(t));

    const medcom = stamkortPayload(jobTitle, subscribers);
    const payload: Parameters<typeof update.mutateAsync>[0] = {
      patient_id: patient.id,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim() || null,
      job_title: medcom.job_title,
      telephone_subscribers: medcom.telephone_subscribers,
      address: {
        street: address.street.trim() || undefined,
        city: address.city.trim() || undefined,
        zip_code: address.zip_code.trim() || undefined,
        country: address.country.trim() || undefined,
      },
      notes: notes.trim() || null,
      cave: cave.trim() || null,
      marketing_consent_email: marketingEmail,
      marketing_consent_sms: marketingSms,
      appointment_email_opt_out: appointmentEmailOptOut,
      preferred_locale: preferredLocale,
    };

    if (canEditCpr && cprChange !== null) {
      if (cprChange === CPR_CLEAR) {
        payload.cpr = null;
      } else {
        const trimmed = cprChange.trim();
        if (trimmed.length > 0) {
          if (!isValidCprEntry(trimmed)) {
            setSubmitError(t('patients.errors.cpr_format'));
            focusPatientField('cpr');
            return;
          }
          payload.cpr = trimmed;
        } else {
          payload.cpr = null;
        }
      }
    }

    if (!fields.canSave || !stamkort.canSave) {
      setFieldsSubmitted(true);
      setSubmitError(t('patients.errors.invalid_input'));
      focusPatientField(fields.email ? firstStamkortFocusId(stamkort) : 'email');
      return;
    }

    try {
      await update.mutateAsync(payload);
      toast.success(t('patients.toast_updated'));
      setEditing(false);
      setFieldsSubmitted(false);
      setCprChange(null);
      setRevealedCpr(null);
    } catch (err) {
      setSubmitError(patientMutationErrorText(err, t));
    }
  };

  const onToggleArchive = async () => {
    const willArchive = patient.status === 'active';
    if (
      !window.confirm(willArchive ? t('patients.confirm_archive') : t('patients.confirm_restore'))
    ) {
      return;
    }
    try {
      await archive.mutateAsync({ patient_id: patient.id, archive: willArchive });
      toast.success(willArchive ? t('patients.toast_archived') : t('patients.toast_restored'));
    } catch (err) {
      toast.error(patientMutationErrorText(err, t));
    }
  };

  const hideRevealedCpr = () => {
    setRevealedCpr(null);
    if (cprHideTimer.current) {
      window.clearTimeout(cprHideTimer.current);
      cprHideTimer.current = null;
    }
  };

  const onRevealCpr = async (reason: string) => {
    try {
      const r = await decrypt.mutateAsync({
        patient_id: patient.id,
        source: 'patient-detail',
        purpose: reason,
      });
      setRevealedCpr(r.cpr);
      setCprModalOpen(false);
      toast.success(t('patients.toast_cpr_revealed'));
      if (cprHideTimer.current) window.clearTimeout(cprHideTimer.current);
      cprHideTimer.current = window.setTimeout(() => {
        setRevealedCpr(null);
        cprHideTimer.current = null;
      }, 60_000);
    } catch (err) {
      toast.error(patientMutationErrorText(err, t));
    }
  };

  const onConvertToPatient = async () => {
    try {
      await update.mutateAsync({ patient_id: patient.id, status: 'active' });
      toast.success(t('patients.relations.toast_converted', 'Now a patient'));
    } catch (err) {
      toast.error(patientMutationErrorText(err, t));
    }
  };

  const editDirty =
    firstName !== patient.first_name ||
    lastName !== patient.last_name ||
    email !== (patient.email ?? '') ||
    jobTitle !== (patient.job_title ?? '') ||
    notes !== (patient.notes ?? '') ||
    cave !== (patient.cave ?? '') ||
    marketingEmail !== patient.marketing_consent_email ||
    marketingSms !== patient.marketing_consent_sms ||
    appointmentEmailOptOut !== Boolean(patient.appointment_email_opt_out) ||
    preferredLocale !== ((patient.preferred_locale as 'da-DK' | 'en-DK') ?? 'da-DK') ||
    cprChange !== null;

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <main className="flex min-h-0 flex-1 flex-col">
        <PageLayout
          fill
          className="overflow-y-auto"
        >
          <PatientHeader
              patient={patient}
              ageYears={ageYears}
              practitionerName={nextAppointment?.clinician_name ?? null}
              canEdit={canEdit}
              canRefer={canReferToHospital}
              canWriteCorrespondence={canReferToHospital}
              canWriteGgop={canWriteGgop}
              canRevealCpr={canRevealCpr}
              revealedCpr={revealedCpr}
              cprLoading={decrypt.isPending}
              infoOpen={!patientInfoCollapsed}
              onEdit={() => setEditing(true)}
              onCorrespondence={() => navigate(patientCorrespondenceNewPath(patient.id))}
              onHospitalReferral={() => navigate(patientHenvisningNewPath(patient.id, 'sygehus'))}
              onRadiologyReferral={() =>
                navigate(patientHenvisningNewPath(patient.id, 'billeddiagnostik'))
              }
              onSpecialistReferral={() =>
                navigate(patientHenvisningNewPath(patient.id, 'speciallaege'))
              }
              onGgop={() => navigate(patientGgopNewPath(patient.id))}
              onRevealCpr={() => (revealedCpr ? hideRevealedCpr() : setCprModalOpen(true))}
              onInfoOpenChange={(open) => setPatientInfoCollapsed(!open)}
              onStartVideoMeeting={
                notRelated && canViewVideoMeetings && canCreateAppointments
                  ? () => {
                      void (async () => {
                        if (!activeClinicId) return;
                        try {
                          const result = await startVideoMeeting.mutateAsync({
                            patientId: patient.id,
                            clinicianId: user?.id,
                            services: services ?? [],
                          });
                          if (result.hostUrl) openExternalMeeting(result.hostUrl);
                          toast.success(t('patients.video_meetings.started'));
                        } catch {
                          toast.error(t('patients.video_meetings.start_error'));
                        }
                      })();
                    }
                  : undefined
              }
              startVideoMeetingPending={startVideoMeeting.isPending}
              moreActions={
                canArchive
                  ? [
                      {
                        label:
                          patient.status === 'active'
                            ? t('patients.archive')
                            : t('patients.restore'),
                        onClick: () => void onToggleArchive(),
                        danger: patient.status === 'active',
                      },
                    ]
                  : undefined
              }
            />

          <EditPatientSlideOver
            open={editing}
            patient={patient}
            firstName={firstName}
            lastName={lastName}
            email={email}
            jobTitle={jobTitle}
            telephoneSubscribers={telephoneSubscribers}
            address={address}
            notes={notes}
            cave={cave}
            marketingEmail={marketingEmail}
            marketingSms={marketingSms}
            appointmentEmailOptOut={appointmentEmailOptOut}
            preferredLocale={preferredLocale}
            cprChange={cprChange}
            canEditCpr={canEditCpr}
            canArchive={canArchive}
            canManagePortal={canEdit}
            fieldErrors={fieldErrors}
            fieldsSubmitted={fieldsSubmitted}
            submitError={submitError}
            saving={update.isPending}
            archiving={archive.isPending}
            dirty={editDirty}
            onClose={() => {
              setEditing(false);
              setSubmitError(null);
              setFieldsSubmitted(false);
              setCprChange(null);
            }}
            onSubmit={onSubmit}
            onArchive={() => void onToggleArchive()}
            setFirstName={setFirstName}
            setLastName={setLastName}
            setEmail={setEmail}
            setJobTitle={setJobTitle}
            setTelephoneSubscribers={setTelephoneSubscribers}
            setAddress={setAddress}
            setNotes={setNotes}
            setCave={setCave}
            setMarketingEmail={setMarketingEmail}
            setMarketingSms={setMarketingSms}
            setAppointmentEmailOptOut={setAppointmentEmailOptOut}
            setPreferredLocale={setPreferredLocale}
            setCprChange={setCprChange}
          />

          <RevealCprModal
            open={cprModalOpen}
            patientFirstName={patient.first_name}
            onClose={() => setCprModalOpen(false)}
            onReveal={onRevealCpr}
            loading={decrypt.isPending}
          />

          {patient.status === 'related' && !editing && (
            <RelatedPersonNotice onConvert={onConvertToPatient} converting={update.isPending} />
          )}

          {activeClinicId && (
            <div className="mt-2 flex min-h-0 flex-1 flex-col">
              {/* Tab Navigation */}
              <div className="mt-s5 shrink-0">
                <SortablePatientTabs
                  tabs={orderedTabs}
                  onReorder={handleTabReorder}
                  visibilityOptions={tabVisibilityOptions}
                  onToggleVisible={handleTabVisibility}
                  canReset={
                    !isDefaultPatientTabOrder(PATIENT_TAB_DEFAULT_ORDER, savedTabOrder) ||
                    hiddenTabs.some((key) =>
                      (PATIENT_TAB_DEFAULT_ORDER as readonly string[]).includes(key),
                    )
                  }
                  onReset={resetTabPrefs}
                />
              </div>

              {/* Tab Content */}
              <div className="mt-s5">
                {tabDenied && <NoAccess />}
                {activeTab === 'relations' && canViewRelations && (
                  <RelationsTab patientId={patient.id} clinicId={activeClinicId} />
                )}
                {activeTab === 'appointments' &&
                  pathTab !== 'henvisning' &&
                  canViewAppointments &&
                  patient.status !== 'related' && (
                    <AppointmentTab patientId={patient.id} clinicId={activeClinicId} />
                  )}
                {activeTab === 'video_meetings' &&
                  canViewVideoMeetings &&
                  patient.status !== 'related' && (
                    <VideoMeetingsTab patientId={patient.id} clinicId={activeClinicId} />
                  )}
                {activeTab === 'treatment_courses' &&
                  canViewTreatmentCourses &&
                  patient.status !== 'related' && (
                    <TreatmentCourseTab
                      patientId={patient.id}
                      clinicId={activeClinicId}
                      canEdit={canEditTreatmentCourses}
                      patientName={`${patient.first_name} ${patient.last_name}`.trim()}
                      currentUserId={user?.id}
                    />
                  )}
                {activeTab === 'activity' && canViewActivity && patient.status !== 'related' && (
                  <ActivityTab patientId={patient.id} clinicId={activeClinicId} />
                )}
                {activeTab === 'patient_reports' &&
                  canViewPatientReports &&
                  patient.status !== 'related' &&
                  activeClinicId && (
                    <PatientReportsTab
                      clinicId={activeClinicId}
                      patientId={patient.id}
                      patientName={`${patient.first_name} ${patient.last_name}`.trim()}
                    />
                  )}
                {activeTab === 'medcom' && canViewMedcom && activeClinicId && (
                  <PatientMedcomWorkspace
                    clinicId={activeClinicId}
                    patient={patient}
                    view={recordView}
                    revealedCpr={revealedCpr}
                    onCprRevealed={setRevealedCpr}
                    canRevealCpr={canRevealCpr}
                    canWriteGgop={canWriteGgop}
                    canViewReferrals={canViewReferrals}
                    canViewGgop={canViewGgop}
                    canViewXdis={canViewXdis}
                    canAttach={isPlatformAdmin || isClinicAdmin(activeClinicId)}
                    onCancel={() => navigate(patientTabPath(patient.id, 'medcom'))}
                    onCreated={() => navigate(patientTabPath(patient.id, 'medcom'))}
                    onGgop={() => navigate(patientGgopNewPath(patient.id))}
                  />
                )}
                {activeTab === 'lab_results' && canViewJournal && activeClinicId && (
                  <PatientLabResultsTab
                    clinicId={activeClinicId}
                    patientId={patient.id}
                    revealedCpr={revealedCpr}
                  />
                )}
                {activeTab === 'journal' && canViewJournal && (
                  <JournalSection
                    clinicId={patient.clinic_id}
                    patientId={patient.id}
                    currentUserId={user?.id}
                    isAdmin={isPlatformAdmin || isClinicAdmin(patient.clinic_id)}
                    appointmentId={journalAppointmentId}
                    entryId={journalEntryId}
                    entryDate={journalEntryDate}
                  />
                )}
                {/* dental view moved to /patients/:id/tand (DentalModulePage) */}
                {activeTab === 'documents' && canViewDocuments && (
                  <PatientDocumentsSection
                    clinicId={activeClinicId}
                    patientId={patient.id}
                    userId={user?.id}
                    isAdmin={isPlatformAdmin || isClinicAdmin(activeClinicId)}
                    canUpload={canUploadDocuments}
                    canUpdate={canUpdateDocuments}
                    canDelete={canDeleteDocuments}
                  />
                )}
                {(activeTab === 'henvisning' || pathTab === 'henvisning') &&
                  patient.status !== 'related' &&
                  canViewHenvisning && (
                    <PatientHenvisningWorkspace
                      clinicId={activeClinicId}
                      patient={patient}
                      view={
                        pathTab === 'henvisning'
                          ? recordView
                          : {
                              kind: 'henvisning-list',
                              box: henvisningBoxFromSearch(searchParams),
                              createdId: searchParams.get('created'),
                            }
                      }
                      revealedCpr={revealedCpr}
                      onCprRevealed={setRevealedCpr}
                      canRevealCpr={canRevealCpr}
                      onCancel={() =>
                        navigate(patientHenvisningPath(patient.id, { vis: 'sendte' }))
                      }
                      onCreated={(created) =>
                        navigate(
                          created.outboxId
                            ? henvisningCreatedDetailPath(patient.id, created.outboxId)
                            : henvisningCreatedPath(patient.id),
                        )
                      }
                    />
                  )}
                {activeTab === 'sagsakter' &&
                  referralsModuleEnabled &&
                  referralId &&
                  canViewReferrals && <PatientReferralSagsakterTab patientId={patient.id} />}
                {activeTab === 'tilknytninger' && canViewAssignments && (
                  <PatientStaffAssignmentsTab
                    clinicId={patient.clinic_id}
                    patientId={patient.id}
                    canEdit={canEditAssignments}
                  />
                )}
                {activeTab === 'invoices' && canViewInvoices && (
                  <InvoiceTab clinicId={patient.clinic_id} patientId={patient.id} />
                )}
                {activeTab === 'punch_cards' && canViewPunchCards && (
                  <PunchCardTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'sygeforsikring' && sdInfo?.enabled && canViewSygeforsikring && (
                  <SygeforsikringTab
                    clinicId={activeClinicId}
                    cpr={revealedCpr}
                    onRequestCprReveal={canRevealCpr ? () => setCprModalOpen(true) : undefined}
                  />
                )}
                {activeTab === 'communication' && canViewCommunication && (
                  <CommunicationTab
                    clinicId={activeClinicId}
                    patientId={patient.id}
                    highlightMessageId={highlightMessageId}
                    highlightThreadId={highlightThreadId}
                    initialTabParam={searchParams.get('tab')}
                    initialChannelParam={searchParams.get('channel')}
                    canViewSms={canViewCommunication}
                    canViewEmail={canViewCommunication}
                    canViewMessages={canViewCommunication}
                    canSendSms={canSendSms}
                    canSendMessages={canSendMessages}
                  />
                )}
                {activeTab === 'consent' && canViewConsent && (
                  <ConsentTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'tasks' && canViewTasks && (
                  <TaskTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'questionnaires' && canViewQuestionnaires && (
                  <FormsAssignmentTab clinicId={activeClinicId} patientId={patient.id} />
                )}
              </div>
            </div>
          )}
        </PageLayout>
      </main>
    </div>
  );
}

/** Default order of the patient page tabs ('dental' opens the dental module page). */
const PATIENT_TAB_DEFAULT_ORDER = [
  'appointments',
  'video_meetings',
  'treatment_courses',
  'relations',
  'journal',
  'lab_results',
  'medcom',
  'dental',
  'documents',
  'henvisning',
  'sagsakter',
  'tilknytninger',
  'invoices',
  'punch_cards',
  'sygeforsikring',
  'communication',
  'consent',
  'tasks',
  'questionnaires',
  'activity',
  // Last on purpose: an unplaced Rapporter stays here, including inside Flere.
  'patient_reports',
] as const;
type PatientTabKey = (typeof PATIENT_TAB_DEFAULT_ORDER)[number];
